import { Button, Select } from '@bloomfieldrobotics/canopy';
import { Box, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';
import { MenuPaper } from '../../../../commonUI/Menu/MenuPaper';
import { useUnits } from '../../../../settings/hooks/useUnits';
import { clusterConsistencyOptions, getBerrySizeOptions } from './constants';

type Props = {
  initClusterConsistency: number;
  initMinSize: number;
  initMaxSize: number;
  onSubmit: (
    clusterConsistency: number,
    minSize: number,
    maxSize: number
  ) => void;
};

/**
 * Uniformity form - an uncontrolled form for inputting, validating,
 * and setting the uniformity filters
 * @param {number} initClusterConsistency - initial cluster consistency
 * @param {number} initMinSize - initial min size - 0 means no min size
 * @param {number} initMaxSize - initial max size - 0 means no max size
 * @param {function} onSubmit - on submit callback, only called if the inputs are valid
 * @returns {React.ReactNode} Uniformity form
 */
export const UniformityForm = ({
  initClusterConsistency,
  initMinSize,
  initMaxSize,
  onSubmit,
}: Props): React.ReactNode => {
  const units = useUnits();
  const [clusterConsistency, setClusterConsistency] = useState(
    initClusterConsistency
  );
  const [minSize, setMinSize] = useState(initMinSize);
  const [maxSize, setMaxSize] = useState(initMaxSize);

  const [didSubmit, setDidSubmit] = useState(false);

  const isInvalid = !clusterConsistency !== (!minSize && !maxSize);

  const handleSubmit = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setDidSubmit(true);

    if (!isInvalid) {
      onSubmit(clusterConsistency, minSize, maxSize);
    }
  };

  // reset max size if user selects a min size greater than the max size
  useEffect(() => {
    if (minSize > maxSize) {
      setMaxSize(0);
    }
  }, [minSize, maxSize]);

  const berrySizeOptions = getBerrySizeOptions(units);

  const minBerrySizeOptions = [{ value: 0, label: 'No Min' }].concat(
    berrySizeOptions
  );

  const maxBerrySizeOptions = [{ value: 0, label: 'No Max' }].concat(
    berrySizeOptions.filter((option) => option.value > minSize || minSize == 0)
  );

  return (
    <MenuPaper>
      <Box component='form' onSubmit={handleSubmit}>
        <Stack spacing={4} sx={{ padding: 1, paddingLeft: 4, paddingRight: 4 }}>
          <Stack spacing={2}>
            <Typography variant='body2'>Cluster consistency</Typography>
            <Select
              options={clusterConsistencyOptions}
              value={clusterConsistency}
              onChange={setClusterConsistency}
              fullWidth
            />
          </Stack>
          <Stack spacing={2}>
            <Typography variant='body2'>Berry size</Typography>
            <Stack
              direction='row'
              spacing={2}
              sx={{
                width: '100%',
                alignItems: 'center',
                justifyContent: 'space-between',
              }}
            >
              <Select
                fullWidth
                options={minBerrySizeOptions}
                value={minSize}
                onChange={setMinSize}
              />
              <Typography variant='body2'>-</Typography>
              <Select
                fullWidth
                options={maxBerrySizeOptions}
                value={maxSize}
                onChange={setMaxSize}
              />
            </Stack>
          </Stack>
          <Button variant='contained' type='submit'>
            Apply
          </Button>
          {didSubmit &&
            isInvalid &&
            (!clusterConsistency ? (
              <Typography variant='body2' color='error'>
                Cluster consistency is required
              </Typography>
            ) : !minSize && !maxSize ? (
              <Typography variant='body2' color='error'>
                Min or Max size required
              </Typography>
            ) : null)}
        </Stack>
      </Box>
    </MenuPaper>
  );
};
